import { ReactNode } from 'react';

import palette from '@lib/core/src/theme/palette';
import { styled, Box, Typography, alpha } from '@mui/material';
import { RiCheckboxCircleFill, RiCheckboxCircleLine } from '@remixicon/react';

const NotifyBoxContainer = styled('button', {
  shouldForwardProp: prop =>
    prop !== 'isAvailable' && prop !== 'isClickable' && prop !== 'isSelected'
})<{ isAvailable: boolean; isClickable: boolean; isSelected: boolean }>(
  ({ isAvailable, isClickable, isSelected }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 8,
    padding: '16px',
    background:
      (isAvailable && isSelected) || (isAvailable && !isClickable)
        ? palette.grey[2400]
        : palette.white,
    border: `1px solid`,
    borderColor:
      (isAvailable && isSelected) || (isAvailable && !isClickable)
        ? palette.black
        : palette.grey[1200],
    borderRadius: 8,
    height: '78px',
    position: 'relative',
    opacity: isAvailable ? 1 : 0.7,
    cursor: isClickable ? 'pointer' : 'default',
    ...(isClickable && {
      '&:hover': {
        background: alpha(palette.black, 0.02)
      }
    })
  })
);

const IconBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'isAvailable'
})<{ isAvailable: boolean }>(({ isAvailable }) => ({
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  overflow: 'hidden',
  opacity: isAvailable ? 1 : 0.7
}));

const CheckIconBox = styled(Box)({
  alignSelf: 'flex-start',
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const SoonBadge = styled(Box)({
  background: palette.white,
  border: `1px solid ${palette.grey[1200]}`,
  borderRadius: 8,
  padding: '4px 16px',
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 11,
  color: palette.grey[400],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const NotifyText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isAvailable'
})<{ isAvailable: boolean }>(({ isAvailable }) => ({
  fontFamily: 'Reddit Sans',
  fontWeight: 500,
  fontSize: 13,
  color: isAvailable ? palette.black : palette.grey[400],
  marginLeft: 8
}));

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8
});

interface NotifyBoxProps {
  icon: ReactNode;
  text: string;
  onClick?: (e: React.MouseEvent) => void;
  isAvailable?: boolean;
  isSelected?: boolean;
  isClickable?: boolean;
}

const NotifyBox = ({
  icon,
  text,
  onClick,
  isAvailable = false,
  isSelected = false,
  isClickable = false
}: NotifyBoxProps) => (
  <NotifyBoxContainer
    isAvailable={isAvailable}
    isClickable={isClickable}
    isSelected={isSelected}
    onClick={onClick}
    type="button"
  >
    <Row>
      <IconBox isAvailable={isAvailable}>{icon}</IconBox>
      <NotifyText isAvailable={isAvailable}>{text}</NotifyText>
    </Row>
    {isClickable && (
      <CheckIconBox>
        {isSelected ? (
          <RiCheckboxCircleFill size={32} color={palette.black} />
        ) : (
          <RiCheckboxCircleLine size={32} color={palette.grey[400]} />
        )}
      </CheckIconBox>
    )}
    {isAvailable && !isClickable && (
      <CheckIconBox>
        <RiCheckboxCircleFill size={32} color={palette.black} />
      </CheckIconBox>
    )}
    {!isAvailable && !isClickable && <SoonBadge>Soon</SoonBadge>}
  </NotifyBoxContainer>
);

export default NotifyBox;
